import { FC, ReactNode, useState } from 'react';
import { IonBadge } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { MarkdownMessage } from '../helper-bee/MarkdownMessage';
import { AICallStatus, IAICallDetail } from './AILogsClient';
import { sourceBadgeColor } from './sourceBadgeColor';

const useStyles = createUseStyles({
    card: {
        border: '1px solid var(--ion-color-step-200, #cccccc)',
        borderRadius: 8,
        marginBottom: 12,
        overflow: 'hidden'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        padding: 12,
        cursor: 'pointer',
        backgroundColor: 'var(--ion-color-step-50, #f7f7f7)'
    },
    headerMeta: {
        fontSize: 12,
        color: 'var(--ion-color-medium)'
    },
    body: {
        padding: 12
    },
    section: {
        marginBottom: 16
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: 13,
        marginBottom: 6
    },
    pre: {
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        padding: 10,
        borderRadius: 6,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 12,
        margin: '6px 0'
    },
    roleLabel: {
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        color: 'var(--ion-color-medium)',
        margin: '10px 0 2px'
    },
    detailsToggle: {
        cursor: 'pointer',
        fontSize: 12,
        color: 'var(--ion-color-primary)',
        userSelect: 'none'
    }
});

const statusColor = (status: AICallStatus): string => {
    if (status === 'ok') {
        return 'success';
    }
    if (status === 'incomplete') {
        return 'warning';
    }
    return 'danger';
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

const asPrintable = (value: unknown): string => {
    if (typeof value === 'string') {
        return value;
    }
    return JSON.stringify(value, null, 2);
};

// One provider item from the request input or response output, rendered by shape: chat
// messages as labeled text, tool calls/results as raw blocks, anything unrecognized as JSON.
const ProviderItem: FC<{ item: unknown }> = ({ item }) => {
    const classes = useStyles();

    if (isRecord(item) && typeof item.role === 'string') {
        const content = typeof item.content === 'string'
            ? item.content
            : asPrintable(item.content);
        return (
            <div>
                <div className={classes.roleLabel}>{item.role}</div>
                {item.role === 'assistant'
                    ? <MarkdownMessage content={content} />
                    : <div className={classes.pre}>{content}</div>}
            </div>
        );
    }

    if (isRecord(item) && item.type === 'function_call') {
        return (
            <div>
                <div className={classes.roleLabel}>tool call: {typeof item.name === 'string' ? item.name : ''}</div>
                <div className={classes.pre}>{asPrintable(item.arguments)}</div>
            </div>
        );
    }

    if (isRecord(item) && item.type === 'function_call_output') {
        return (
            <div>
                <div className={classes.roleLabel}>tool result</div>
                <div className={classes.pre}>{asPrintable(item.output)}</div>
            </div>
        );
    }

    if (isRecord(item) && item.type === 'reasoning') {
        // Reasoning items carry only encrypted content; the marker shows one occurred.
        return <div className={classes.roleLabel}>[model reasoning]</div>;
    }

    if (isRecord(item) && item.type === 'message') {
        return <ProviderItem item={{ ...item, role: 'assistant' }} />;
    }

    return <div className={classes.pre}>{asPrintable(item)}</div>;
};

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => {
    const classes = useStyles();
    return (
        <div className={classes.section}>
            <div className={classes.sectionTitle}>{title}</div>
            {children}
        </div>
    );
};

const CollapsibleText: FC<{ label: string; text: string }> = ({ label, text }) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div>
            <div className={classes.detailsToggle} onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? `Hide ${label}` : `Show ${label} (${text.length.toLocaleString()} chars)`}
            </div>
            {isOpen && <div className={classes.pre}>{text}</div>}
        </div>
    );
};

const CardHeader: FC<{ call: IAICallDetail; onToggle: () => void }> = ({ call, onToggle }) => {
    const classes = useStyles();
    return (
        <div className={classes.header} onClick={onToggle} data-id="ai-call-header">
            <IonBadge color={sourceBadgeColor(call.source)}>{call.source}</IonBadge>
            <IonBadge color={statusColor(call.status)}>{call.status}</IonBadge>
            <span className={classes.headerMeta}>{new Date(call.createdAt).toLocaleString()}</span>
            <span className={classes.headerMeta}>{call.model}</span>
            <span className={classes.headerMeta}>{(call.latencyMs / 1000).toFixed(1)}s</span>
            {call.inputTokens !== undefined &&
                <span className={classes.headerMeta}>{call.inputTokens} in / {call.outputTokens ?? 0} out</span>
            }
            {call.toolNames && call.toolNames.length > 0 &&
                <span className={classes.headerMeta}>tools: {call.toolNames.join(', ')}</span>
            }
        </div>
    );
};

export const AICallDetailCard: FC<{ call: IAICallDetail }> = ({ call }) => {
    const classes = useStyles();
    const [isExpanded, setIsExpanded] = useState(false);

    const requestInput = call.payload?.request.input;
    const inputItems: unknown[] = Array.isArray(requestInput) ? requestInput : [];
    const responseOutputItems = call.payload?.response?.outputItems;
    const outputItems: unknown[] = Array.isArray(responseOutputItems) ? responseOutputItems : [];

    return (
        <div className={classes.card}>
            <CardHeader call={call} onToggle={() => setIsExpanded(!isExpanded)} />

            {isExpanded &&
                <div className={classes.body}>
                    {call.errorMessage &&
                        <Section title="Error"><div className={classes.pre}>{call.errorMessage}</div></Section>
                    }

                    {call.payloadMissing &&
                        <Section title="Payload unavailable">
                            <div className={classes.headerMeta}>The stored request/response has expired or could not be fetched.</div>
                        </Section>
                    }

                    {call.payload?.request.instructions &&
                        <Section title="Instructions (system prompt)">
                            <CollapsibleText label="instructions" text={call.payload.request.instructions} />
                        </Section>
                    }

                    {call.payload && !Array.isArray(requestInput) &&
                        <Section title="Input"><div className={classes.pre}>{asPrintable(requestInput)}</div></Section>
                    }

                    {inputItems.length > 0 &&
                        <Section title={`Input items (${inputItems.length})`}>
                            {inputItems.map((item, index) => <ProviderItem key={index} item={item} />)}
                        </Section>
                    }

                    {call.payload?.response?.outputText &&
                        <Section title="Output"><MarkdownMessage content={call.payload.response.outputText} /></Section>
                    }

                    {outputItems.length > 0 &&
                        <Section title={`Output items (${outputItems.length})`}>
                            {outputItems.map((item, index) => <ProviderItem key={index} item={item} />)}
                        </Section>
                    }

                    {call.payload?.response?.usage !== undefined &&
                        <Section title="Usage"><div className={classes.pre}>{asPrintable(call.payload.response.usage)}</div></Section>
                    }
                </div>
            }
        </div>
    );
};
